import { useEffect, useState } from "react";
import { BarChart3, TrendingUp, TrendingDown, Minus, Calendar, Target, Flame, Layers } from "lucide-react";
import client from "../lib/api";
import { MOOD_BY_KEY } from "./moods";
import MoodFace from "./MoodFace";
import "./moods.css";

// A private, traceable read of how the person has been feeling. Reads
// /engagement/mood-summary, which counts EVERY entry (not one per day),
// and shows the mood mix for this month / last month / overall, a
// this-month highlights block, a month-by-month strip, and a durable
// monthly log. Renders nothing until at least one mood is logged. Never a
// score.

const RANGES = [
  { key: "this_month", label: "This month", word: "this month" },
  { key: "last_month", label: "Last month", word: "last month" },
  { key: "overall", label: "Overall", word: "all time" },
];

function feelingWord(avg) {
  if (avg == null) return null;
  if (avg >= 4.3) return "bright";
  if (avg >= 3.6) return "good";
  if (avg >= 2.6) return "mixed";
  if (avg >= 1.8) return "heavy";
  return "rough";
}

const MONTHS_SHORT = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function shortDate(iso) {
  if (!iso) return "";
  const p = String(iso).split("-");
  if (p.length !== 3) return iso;
  return `${MONTHS_SHORT[parseInt(p[1], 10)] || p[1]} ${parseInt(p[2], 10)}`;
}

export default function MoodInsights({ refreshKey = 0, testid = "mood-insights" }) {
  const [data, setData] = useState(null);
  const [range, setRange] = useState("this_month");

  useEffect(() => {
    let alive = true;
    client.get("/engagement/mood-summary?months=6")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [refreshKey]);

  if (!data || !data.has_data) return null;

  const r = data.ranges[range] || { total: 0, entries: 0, days: 0, counts: [], dominant: null, avg_score: null };
  const entries = r.entries != null ? r.entries : r.total;
  const rangeMeta = RANGES.find((x) => x.key === range);
  const maxCount = r.counts.reduce((a, c) => Math.max(a, c.count), 0) || 1;
  const dom = r.dominant ? MOOD_BY_KEY[r.dominant] : null;
  const feel = feelingWord(r.avg_score);

  const tm = data.ranges.this_month ? data.ranges.this_month.avg_score : null;
  const lm = data.ranges.last_month ? data.ranges.last_month.avg_score : null;
  let cmp = null;
  if (tm != null && lm != null) {
    const d = tm - lm;
    cmp = d > 0.3 ? { word: "lifting vs last month", up: true }
      : d < -0.3 ? { word: "dipping vs last month", up: false }
        : { word: "steady vs last month", up: null };
  }

  const months = data.months || [];
  const log = data.log || [];
  const hl = data.highlights || null;
  const hlDom = hl && hl.dominant ? MOOD_BY_KEY[hl.dominant] : null;

  return (
    <div className="k-card" data-testid={testid}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="k-label flex items-center gap-2 !mb-0"><BarChart3 size={13} /> how you have been feeling</div>
        <div className="mf-seg" role="tablist" aria-label="range">
          {RANGES.map((x) => (
            <button
              key={x.key}
              type="button"
              role="tab"
              aria-selected={range === x.key}
              className={`mf-seg-btn ${range === x.key ? "is-on" : ""}`}
              onClick={() => setRange(x.key)}
              data-testid={`${testid}-range-${x.key}`}
            >
              {x.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="klareet-serif" style={{ fontSize: "1.15rem", color: "hsl(var(--klareet-ink))" }} data-testid={`${testid}-headline`}>
          {entries} {entries === 1 ? "entry" : "entries"} {rangeMeta.word}
          {r.days ? <> · across {r.days} {r.days === 1 ? "day" : "days"}</> : ""}
          {dom ? <> · you leaned <span style={{ color: dom.c2, fontWeight: 600 }}>{dom.label}</span></> : ""}
        </div>
        {feel && (
          <span className="k-chip !text-xs" style={{ background: dom ? `${dom.c2}14` : undefined, color: dom ? dom.c2 : undefined, border: "none" }}>
            feeling {feel}
          </span>
        )}
        {range === "this_month" && cmp && (
          <span className="k-chip !text-xs flex items-center gap-1" style={{ border: "none", background: "hsl(var(--klareet-canvas-2))" }} data-testid={`${testid}-compare`}>
            {cmp.up === true ? <TrendingUp size={12} /> : cmp.up === false ? <TrendingDown size={12} /> : <Minus size={12} />} {cmp.word}
          </span>
        )}
      </div>

      {r.counts.length === 0 ? (
        <div className="k-quiet !text-sm py-3" data-testid={`${testid}-empty`}>no moods logged {rangeMeta.word} yet.</div>
      ) : (
        <div className="space-y-2" data-testid={`${testid}-dist`}>
          {r.counts.map((c) => {
            const m = MOOD_BY_KEY[c.mood];
            if (!m) return null;
            const pct = entries ? Math.round((c.count / entries) * 100) : 0;
            const w = Math.max(6, Math.round((c.count / maxCount) * 100));
            return (
              <div
                key={c.mood}
                className="flex items-center gap-3"
                title={`${m.label}: ${c.count} of ${entries} entries (${pct}%)`}
                data-testid={`${testid}-row-${c.mood}`}
              >
                <span className="flex items-center gap-1.5" style={{ width: 96, flex: "0 0 auto" }}>
                  <MoodFace moodKey={c.mood} size={20} animate={false} />
                  <span className="text-xs" style={{ color: "hsl(var(--klareet-ink-soft))" }}>{m.label}</span>
                </span>
                <span className="flex-1 rounded-full overflow-hidden" style={{ height: 12, background: "hsl(var(--klareet-canvas-2))" }}>
                  <span style={{ display: "block", height: "100%", width: `${w}%`, background: m.c2, borderRadius: 999 }} />
                </span>
                <span className="klareet-mono text-xs" style={{ width: 66, textAlign: "right", color: "hsl(var(--klareet-ink))", fontVariantNumeric: "tabular-nums" }}>
                  {c.count} · {pct}%
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* This month at a glance */}
      {hl && hl.entries > 0 && (
        <div className="mt-6 pt-4" style={{ borderTop: "1px solid hsl(var(--klareet-line-soft))" }}>
          <div className="k-label !mb-3">{hl.month_label} at a glance</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid={`${testid}-highlights`}>
            <StatTile icon={<BarChart3 size={13} />} label="entries" value={hl.entries} />
            <StatTile icon={<Calendar size={13} />} label="days logged" value={hl.days} />
            <StatTile
              icon={hlDom ? <MoodFace moodKey={hl.dominant} size={16} animate={false} /> : <Minus size={13} />}
              label="leaned"
              value={hlDom ? hlDom.label : "even"}
              color={hlDom ? hlDom.c2 : undefined}
            />
            {hl.feeling && <StatTile icon={<TrendingUp size={13} />} label="feeling" value={hl.feeling} />}
            <StatTile icon={<Target size={13} />} label="goals made" value={hl.goals_made} />
            {hl.top_day && (
              <StatTile icon={<Flame size={13} />} label={`busiest · ${shortDate(hl.top_day.day)}`} value={`${hl.top_day.count} ${hl.top_day.count === 1 ? "entry" : "entries"}`} />
            )}
          </div>
        </div>
      )}

      {months.length > 0 && (
        <div className="mt-6 pt-4" style={{ borderTop: "1px solid hsl(var(--klareet-line-soft))" }}>
          <div className="k-label !mb-3">month by month</div>
          <div className="flex items-end justify-between gap-2" data-testid={`${testid}-months`}>
            {months.map((mo) => {
              const meta = mo.dominant ? MOOD_BY_KEY[mo.dominant] : null;
              const h = mo.avg_score ? (mo.avg_score / 5) * 100 : 0;
              const mEntries = mo.entries != null ? mo.entries : mo.total;
              return (
                <div
                  key={mo.key}
                  className="flex-1 flex flex-col items-center gap-1.5"
                  title={`${mo.label} ${mo.year}${mo.is_current ? " (this month)" : ""}: ${mEntries} ${mEntries === 1 ? "entry" : "entries"}${meta ? ", leaning " + meta.label : ""}${mo.avg_score ? ", avg " + mo.avg_score : ""}`}
                  data-testid={`${testid}-month-${mo.key}`}
                >
                  <div className="w-full relative rounded-lg overflow-hidden flex items-end" style={{ height: 64, background: "hsl(var(--klareet-canvas-2))" }}>
                    {meta ? (
                      <div className="w-full rounded-lg transition-all duration-500" style={{ height: `${Math.max(12, h)}%`, background: meta.c2 }} />
                    ) : (
                      <div className="w-full flex items-end justify-center pb-1.5"><span className="w-1 h-1 rounded-full" style={{ background: "hsl(var(--klareet-line))" }} /></div>
                    )}
                  </div>
                  <span className="k-quiet !text-[10px]" style={{ fontWeight: mo.is_current ? 600 : 400, color: mo.is_current ? "hsl(var(--klareet-ink))" : undefined }}>{mo.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Durable monthly log */}
      {log.length > 0 && (
        <div className="mt-6 pt-4" style={{ borderTop: "1px solid hsl(var(--klareet-line-soft))" }}>
          <div className="k-label !mb-3 flex items-center gap-2"><Layers size={13} /> your monthly log</div>
          <div className="space-y-2" style={{ maxHeight: 260, overflowY: "auto" }} data-testid={`${testid}-log`}>
            {log.map((mo) => {
              const meta = mo.dominant ? MOOD_BY_KEY[mo.dominant] : null;
              const mEntries = mo.entries != null ? mo.entries : mo.total;
              return (
                <div
                  key={mo.key}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5"
                  style={{ background: meta ? `${meta.c2}0e` : "hsl(var(--klareet-canvas-2))", border: `1px solid ${meta ? `${meta.c2}26` : "hsl(var(--klareet-line-soft))"}` }}
                  data-testid={`${testid}-log-${mo.key}`}
                >
                  <span className="shrink-0">
                    {meta ? <MoodFace moodKey={mo.dominant} size={26} animate={false} /> : <span className="inline-block w-[26px] h-[26px] rounded-full" style={{ background: "hsl(var(--klareet-canvas-2))" }} />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>
                      {mo.label} {mo.year}{mo.is_current ? <span className="k-quiet !text-[11px] font-normal"> · this month</span> : ""}
                    </div>
                    <div className="k-quiet !text-xs mt-0.5">
                      {mEntries} {mEntries === 1 ? "entry" : "entries"} · {mo.days} {mo.days === 1 ? "day" : "days"}
                      {meta ? <> · leaned <span style={{ color: meta.c2, fontWeight: 600 }}>{meta.label}</span></> : ""}
                      {mo.feeling ? ` · feeling ${mo.feeling}` : ""}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="k-quiet !text-[11px] mt-4" style={{ lineHeight: 1.5 }}>
        You choose each mood yourself. For the trend, each mood maps to a 1-to-5 scale and we average them. All private to you, never a score.
      </p>
    </div>
  );
}


function StatTile({ icon, label, value, color }) {
  return (
    <div className="rounded-xl px-3 py-2.5" style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))" }}>
      <div className="flex items-center gap-1.5 k-quiet !text-[10px]" style={{ textTransform: "uppercase", letterSpacing: ".06em", fontWeight: 700 }}>
        {icon} {label}
      </div>
      <div className="mt-1 font-bold" style={{ fontSize: "1.05rem", color: color || "hsl(var(--klareet-ink))", lineHeight: 1.1 }}>
        {value}
      </div>
    </div>
  );
}
